import { useEffect, useState } from "react";

const isElementReference = (reference: unknown): reference is Element => {
  return typeof Element !== "undefined" && reference instanceof Element;
};

interface UseDismissTooltipOnScrollAwayOptions {
  open: boolean;
  forceOpen?: boolean;
  referenceElement: unknown;
  floatingElement?: HTMLElement | null;
  isPointerOverReference: () => boolean;
  onOpenChange: (open: boolean) => void;
}

export const useDismissTooltipOnScrollAway = ({
  open,
  forceOpen,
  referenceElement,
  floatingElement,
  isPointerOverReference,
  onOpenChange,
}: UseDismissTooltipOnScrollAwayOptions) => {
  useEffect(() => {
    if (!open || forceOpen) {
      return;
    }

    const ownerDocument = isElementReference(referenceElement)
      ? referenceElement.ownerDocument
      : document;
    const handleScroll = (event: Event) => {
      if (event.target instanceof Node && floatingElement?.contains(event.target)) {
        return;
      }
      if (!isPointerOverReference()) {
        onOpenChange(false);
      }
    };

    ownerDocument.addEventListener("scroll", handleScroll, { capture: true, passive: true });

    return () => {
      ownerDocument.removeEventListener("scroll", handleScroll, true);
    };
  }, [floatingElement, forceOpen, isPointerOverReference, onOpenChange, open, referenceElement]);
};

/**
 * Whether `root` or anything inside it carries `aria-expanded="true"`: a dropdown trigger with its
 * menu open. The attribute flips without a render of the caller, so it is observed rather than
 * read once. Always false while `enabled` is off, with no observer attached.
 */
export const useDescendantExpanded = (root: unknown, enabled: boolean): boolean => {
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (!enabled || !isElementReference(root)) {
      setExpanded(false);
      return;
    }

    const selector = '[aria-expanded="true"]';
    const update = () => {
      setExpanded(root.matches(selector) || root.querySelector(selector) != null);
    };
    update();

    const observer = new MutationObserver(update);
    observer.observe(root, {
      attributes: true,
      attributeFilter: ["aria-expanded"],
      childList: true,
      subtree: true,
    });

    return () => observer.disconnect();
  }, [enabled, root]);

  return expanded;
};
